import { useMemo } from 'react';

import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	prefetchQuery,
	useQuery,
} from '@atlassian/conversation-assistant-service/common/utils/use-query';

import type {
	RecommendedSourceDTO,
	RecommendedSpacesFetchParams,
	RecommendedSpacesResponse,
	RecommendedThreadDTO,
} from './useRecommendedSpaces.fetch';
import { recommendedSpacesFetchAction } from './useRecommendedSpaces.fetch';

// ---------------------------------------------------------------------------
// Local ThreadSource / ThreadNode types
// ---------------------------------------------------------------------------
// These mirror the shapes defined in rovo-navigation's homeThreadsService.
// We duplicate them here to avoid a circular dependency
// (rovo-navigation → rovo-spaces → rovo-navigation).

export interface RecommendedThreadSource {
	__typename: string;
	ari?: string;
	url?: string;
	data?: { __typename: string } | null;
}

export interface RecommendedThreadNode {
	id: string;
	title: string;
	sources: RecommendedThreadSource[];
}

/**
 * Derives a sourceType string from an ARI.
 *
 * ARIs follow the pattern `ari:cloud:<product>:<cloudId>:<resourceType>/<id>`.
 * We extract the `<product>` segment (e.g. "confluence", "jira").
 */
const getSourceTypeFromAri = (ari: string): string => {
	const parts = ari.split(':');
	// ari:cloud:<product>:<cloudId>:<resourceType>/<id>
	if (parts.length >= 3) {
		return parts[2].toLowerCase();
	}
	return 'unknown';
};

/**
 * Derives a sourceType string from a URL's hostname.
 */
const getSourceTypeFromUrl = (url: string): string => {
	try {
		const hostname = new URL(url).hostname;
		if (hostname.includes('atlassian.net') && url.includes('/browse/')) {
			return 'jira';
		}
		if (hostname.includes('atlassian.net') && url.includes('/wiki/')) {
			return 'confluence';
		}
		if (hostname.includes('bitbucket')) {
			return 'bitbucket';
		}
		if (hostname.includes('loom')) {
			return 'loom';
		}
		if (hostname.includes('github')) {
			return 'github';
		}
	} catch {
		// fall through
	}
	return 'unknown';
};

/**
 * Maps a REST `SourceDTO` into the `ThreadSource` shape that the existing UI
 * helpers (`extractSourcesFromThread`, etc.) expect.
 *
 * The GraphQL schema uses union types (`ConvoAiHomeThreadsFirstPartySource` /
 * `ConvoAiHomeThreadsThirdPartySource`) with `__typename` discriminators.
 * The REST endpoint returns flat `{ url, ari }` objects — we synthesize the
 * correct `__typename` and nested `data.__typename` so downstream code works
 * unchanged.
 */
const mapSourceToThreadSource = (source: RecommendedSourceDTO): RecommendedThreadSource => {
	const sourceType = source.ari
		? getSourceTypeFromAri(source.ari)
		: getSourceTypeFromUrl(source.url);

	// If the source has an ARI it's a first-party Atlassian source; otherwise
	// treat it as third-party.
	const isFirstParty =
		source.ari &&
		(sourceType === 'confluence' || sourceType === 'jira' || sourceType === 'townsquare');

	if (isFirstParty) {
		// Map the product name to the GraphQL data __typename convention:
		// confluence → ConvoAiHomeThreadsConfluenceData, jira → ConvoAiHomeThreadsJiraData, etc.
		const capitalized = sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
		return {
			__typename: 'ConvoAiHomeThreadsFirstPartySource',
			ari: source.ari,
			url: source.url,
			data: { __typename: `ConvoAiHomeThreads${capitalized}Data` },
		};
	}

	return {
		__typename: 'ConvoAiHomeThreadsThirdPartySource',
		url: source.url,
	};
};

/**
 * Maps a REST `HomeThreadDTO` into the `ThreadNode` shape used by UI components.
 */
const mapThreadDTOToThreadNode = (thread: RecommendedThreadDTO): RecommendedThreadNode => ({
	id: thread.id,
	title: thread.title,
	sources: thread.sources.map(mapSourceToThreadSource),
});

type UseRecommendedSpacesProps = RecommendedSpacesFetchParams & {
	/**
	 * When `true`, the hook skips the network call entirely.
	 */
	skip?: boolean;
};

type UseRecommendedSpacesResult = {
	threads: RecommendedThreadNode[];
	isLoading: boolean;
	error: Error | null;
};

const getRecommendedSpacesQueryKey = ({
	cloudId,
	product,
	experienceId,
}: RecommendedSpacesFetchParams) => [
	'rovo-spaces:recommendedSpaces',
	cloudId,
	product,
	experienceId,
];

export const prefetchRecommendedSpaces = ({
	cloudId,
	product,
	experienceId,
	onStart,
	onSuccess,
	onError,
}: RecommendedSpacesFetchParams & {
	onStart?: () => void;
	onSuccess?: (response: RecommendedSpacesResponse) => void;
	onError?: (error: ApiError) => void;
}): void => {
	prefetchQuery({
		queryKey: getRecommendedSpacesQueryKey({ cloudId, product, experienceId }),
		queryFn: () => recommendedSpacesFetchAction({ cloudId, product, experienceId }),
		onStart,
		onSuccess,
		onError,
	});
};

/**
 * Fetches recommended space threads from the REST API
 * (`GET /api/rovo/v1/spaces/recommended`) and maps them into the `ThreadNode`
 * shape consumed by `RecommendedSpacesPanel` and `CreateSpaceRecommendedCard`.
 *
 * This is the REST-backed replacement for the `convoai_homeThreads` GraphQL
 * query. The backend handles server-side filtering of threads the user has
 * already converted into spaces.
 */
export const useRecommendedSpaces = ({
	cloudId,
	product,
	experienceId,
	skip = false,
}: UseRecommendedSpacesProps): UseRecommendedSpacesResult => {
	const { data, isFetching, error } = useQuery<RecommendedSpacesResponse>({
		queryKey: getRecommendedSpacesQueryKey({ cloudId, product, experienceId }),
		queryFn: () => recommendedSpacesFetchAction({ cloudId, product, experienceId }),
		enabled: !skip,
	});

	const threads: RecommendedThreadNode[] = useMemo(() => {
		if (!data?.threads) {
			return [];
		}
		return data.threads.map(mapThreadDTOToThreadNode);
	}, [data]);

	return useMemo(
		() => ({ threads, isLoading: isFetching, error: error ?? null }),
		[threads, isFetching, error],
	);
};
